import { graphql } from 'react-relay';
import { type PayloadError, type RecordProxy, commitLocalUpdate, commitMutation } from 'relay-runtime';
import type { DeepNonNullable } from 'ts-essentials';
import { RelayEnvironment } from '../relay-env';
import { genericMutation } from '../utils';
import type {
  Integration_HsUpdateSettings_Mutation,
  Integration_HsUpdateSettings_Mutation$data,
} from './__generated__/Integration_HsUpdateSettings_Mutation.graphql';
import type {
  Integration_HubspotDeleteConnection_Mutation,
  Integration_HubspotDeleteConnection_Mutation$data,
} from './__generated__/Integration_HubspotDeleteConnection_Mutation.graphql';
import type { Integration_HubspotRegisterOauth_Mutation } from './__generated__/Integration_HubspotRegisterOauth_Mutation.graphql';
import type { Integration_MarketoUpdateSettings_Mutation } from './__generated__/Integration_MarketoUpdateSettings_Mutation.graphql';
import type { Integration_MicrosoftRegister_Mutation } from './__generated__/Integration_MicrosoftRegister_Mutation.graphql';
import type { Integration_MicrosoftRemoveAuth_Mutation } from './__generated__/Integration_MicrosoftRemoveAuth_Mutation.graphql';
import type { Integration_ZohoDeleteAuth_Mutation } from './__generated__/Integration_ZohoDeleteAuth_Mutation.graphql';
import type { Integration_ZohoRegisterAuth_Mutation } from './__generated__/Integration_ZohoRegisterAuth_Mutation.graphql';
import type { Integration_ZohoUpdateSettings_Mutation } from './__generated__/Integration_ZohoUpdateSettings_Mutation.graphql';

/**
 * Zoho
 */

export * from './__generated__/Integration_ZohoRegisterAuth_Mutation.graphql';
const ZohoRegisterAuthMutation = graphql`
  mutation Integration_ZohoRegisterAuth_Mutation($input: ZohoRegisterAuthInput!) {
    zohoRegisterAuth(input: $input) {
      success
    }
  }
`;

export const zohoRegisterAuth = genericMutation<Integration_ZohoRegisterAuth_Mutation>({
  mutation: ZohoRegisterAuthMutation,
});

export * from './__generated__/Integration_ZohoDeleteAuth_Mutation.graphql';
const ZohoDeleteAuthMutation = graphql`
  mutation Integration_ZohoDeleteAuth_Mutation($input: ZohoDeleteAuthInput!) {
    zohoDeleteAuth(input: $input) {
      success
    }
  }
`;

export const zohoDeleteAuth = genericMutation<Integration_ZohoDeleteAuth_Mutation>({
  mutation: ZohoDeleteAuthMutation,
});

export * from './__generated__/Integration_ZohoUpdateSettings_Mutation.graphql';
const ZohoUpdateSettingsMutation = graphql`
  mutation Integration_ZohoUpdateSettings_Mutation($input: ZohoUpdateSettingsInput!) {
    zohoUpdateSettings(input: $input) {
      salesIqPortalId
      salesIqAppId
    }
  }
`;

export const zohoUpdateSettings = genericMutation<Integration_ZohoUpdateSettings_Mutation>({
  mutation: ZohoUpdateSettingsMutation,
});

/**
 * Microsoft
 */

const MsRegisterMutation = graphql`
  mutation Integration_MicrosoftRegister_Mutation($input: MsAuthenticateTenantInput!) {
    msAuthenticateTenant(input: $input) {
      success
    }
  }
`;

export * from './__generated__/Integration_MicrosoftRegister_Mutation.graphql';

export const msRegisterMutation = genericMutation<Integration_MicrosoftRegister_Mutation>({
  mutation: MsRegisterMutation,
});

const MsRemoveAuthMutation = graphql`
  mutation Integration_MicrosoftRemoveAuth_Mutation($input: MsRemoveTenantInput!) {
    msRemoveTenant(input: $input) {
      success
    }
  }
`;

export * from './__generated__/Integration_MicrosoftRemoveAuth_Mutation.graphql';

export const msRemoveAuthMutation = genericMutation<Integration_MicrosoftRemoveAuth_Mutation>({
  mutation: MsRemoveAuthMutation,
});

/**
 * Hubspot
 */

const HsRegisterOauthMutation = graphql`
  mutation Integration_HubspotRegisterOauth_Mutation($input: HsRegisterOauthTokensInput!) {
    hsRegisterOauthTokens(input: $input) {
      success
    }
  }
`;

export const hubspotRegisterOauthMutation = genericMutation<Integration_HubspotRegisterOauth_Mutation>({
  mutation: HsRegisterOauthMutation,
});

const HsDeleteConnectionMutation = graphql`
  mutation Integration_HubspotDeleteConnection_Mutation($input: HsDeleteConnectionInput!) {
    hsDeleteConnection(input: $input) {
      success
    }
  }
`;

export const hsDeleteConnection = (
  input: Integration_HubspotDeleteConnection_Mutation['variables']['input'],
  onCompleted?: (
    response: Integration_HubspotDeleteConnection_Mutation$data | null,
    errors: readonly PayloadError[] | null | undefined
  ) => void
) => {
  commitMutation<Integration_HubspotDeleteConnection_Mutation>(RelayEnvironment, {
    mutation: HsDeleteConnectionMutation,
    variables: {
      input,
    },
    onError: (error) => {
      onCompleted?.(null, [error]);
    },
    onCompleted: (response, errors) => {
      onCompleted?.(response, errors);

      if (!errors?.length)
        // we want to update the local store to remove the hubspotTracker
        commitLocalUpdate(RelayEnvironment, (store) => {
          const root = store.getRoot();
          const znglyInfo = root
            ?.getLinkedRecord('znglyInfo(id:"site-info",idType:"SLUG")')
            ?.getLinkedRecord('znglyInfo');

          if (!!znglyInfo) {
            // we want to update hubspotTracker to null
            znglyInfo.setValue(null, 'hubspotTracker');
          }
        });
    },
  });
};

const UpdateHubspotSettingsMutation = graphql`
  mutation Integration_HsUpdateSettings_Mutation($input: HsUpdateSettingsInput!) {
    hsUpdateSettings(input: $input) {
      allowedContactProperties {
        key
        value
      }
      contactProperties {
        key
        value
      }
    }
  }
`;

export * from './__generated__/Integration_HsUpdateSettings_Mutation.graphql';
export const hsUpdateSettings = (
  input: Integration_HsUpdateSettings_Mutation['variables']['input'],
  onCompleted?: (
    response: Integration_HsUpdateSettings_Mutation$data | null,
    errors: readonly PayloadError[] | null | undefined
  ) => void
) => {
  commitMutation<Integration_HsUpdateSettings_Mutation>(RelayEnvironment, {
    mutation: UpdateHubspotSettingsMutation,
    variables: {
      input,
    },
    onCompleted,
    onError: (error) => {
      onCompleted?.(null, [error]);
    },
    updater: (store, response) => {
      const root = store.getRoot();
      const hsInfo = root?.getLinkedRecord('hsInfo');
      if (!hsInfo) return;

      type ContactPropertiesRecords = RecordProxy<
        DeepNonNullable<Integration_HsUpdateSettings_Mutation$data>['hsUpdateSettings']['contactProperties']
      >[];

      const oldContactPropertiesRecords = hsInfo?.getLinkedRecords('contactProperties') as ContactPropertiesRecords;

      // if the old records are the same as the new ones, we don't need to update anything
      if (
        oldContactPropertiesRecords?.length === response?.hsUpdateSettings?.contactProperties?.length &&
        oldContactPropertiesRecords.every((record, idx) => {
          const newRecord = response.hsUpdateSettings?.contactProperties?.[idx];
          return record.getValue('key') === newRecord?.key && record.getValue('value') === newRecord?.value;
        })
      )
        return;

      const contactPropertiesRecords = (response?.hsUpdateSettings?.contactProperties ?? [])
        .map((prop, idx) => {
          // if the record already exists, we want to update it
          if (oldContactPropertiesRecords?.[idx]) {
            return oldContactPropertiesRecords[idx].setValue(prop?.key, 'key').setValue(prop?.value, 'value');
          }

          // create a new record for each contact property
          return store
            .create(`${hsInfo.getDataID()}:contactProperties:${idx}`, 'JSONKeyValue')
            .setValue(prop?.key, 'key')
            .setValue(prop?.value, 'value');
        })
        .slice(0, response?.hsUpdateSettings?.contactProperties?.length ?? 0) as ContactPropertiesRecords;

      // if there are less records than before, we want to remove the extra ones
      if (oldContactPropertiesRecords?.length > contactPropertiesRecords.length) {
        oldContactPropertiesRecords.slice(contactPropertiesRecords.length).forEach((record) => {
          store.delete(record.getDataID());
        });
      }

      hsInfo.setLinkedRecords(contactPropertiesRecords, 'contactProperties');
    },
  });
};

/**
 * Marketo
 */

const MarketoUpdateSettingsMutation = graphql`
  mutation Integration_MarketoUpdateSettings_Mutation($input: MarketoUpdateSettingsInput!) {
    marketoUpdateSettings(input: $input) {
      settings {
        ...Integration_MarketoInfo_Fragment @relay(mask: false)
      }
    }
  }
`;

export const marketoUpdateSettings = genericMutation<Integration_MarketoUpdateSettings_Mutation>({
  mutation: MarketoUpdateSettingsMutation,
});
